import { CompactDialogContent } from '../../ui/CompactDialogContent.js';
import { formatCrmMoney, formatCrmMoneyInput } from './crm-money.js';
import * as Dialog from '@radix-ui/react-dialog';
import type { ShootTransaction } from '@diana/contracts';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useEditPanelViewport } from '../../ui/use-edit-panel-viewport.js';
import { Plus, X, Pencil, Trash2 } from 'lucide-react';
import { useDianaUIPortal } from '../../ui/index.js';
import { financeSummary, parseTransactionAmount, todayInLisbon } from './shoot-finance.js';
import './ShootMoney.css';

type FormValue = {
  id: string;
  kind: 'Income' | 'Expense';
  amount: string;
  date: string;
  description: string;
  purpose: 'Deposit' | 'Balance' | 'Other';
};
export function ShootMoney({
  price,
  currency,
  items,
  busy,
  disabled,
  onSave,
  onDirtyChange,
  onReset,
  onBeforeOpen,
}: {
  price: number | null;
  currency: string;
  items: readonly ShootTransaction[];
  busy: boolean;
  disabled: boolean;
  onSave(items: ShootTransaction[]): Promise<boolean>;
  onDirtyChange(dirty: boolean): void;
  onReset(): void;
  onBeforeOpen(): boolean;
}) {
  const portal = useDianaUIPortal();
  const [history, setHistory] = useState(false);
  const [form, setForm] = useState<FormValue | null>(null);
  const lastForm = useRef<FormValue | null>(null);
  if (form) lastForm.current = form;
  const displayForm = form ?? lastForm.current;
  const content = useRef<HTMLDivElement>(null);
  const { panelRef, bodyRef } = useEditPanelViewport(form !== null);
  const capturePanel = useCallback(
    (node: HTMLDivElement | null) => {
      content.current = node;
      panelRef(node);
    },
    [panelRef],
  );
  const [editingTransaction, setEditingTransaction] = useState(false);
  const [original, setOriginal] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<ShootTransaction | null>(null);
  const summary = financeSummary(price, items);
  const paid = price !== null && summary.remaining === 0;
  const money = (value: number | null) => formatCrmMoney(value, currency);
  const dirty = Boolean(form && JSON.stringify(form) !== original);
  useEffect(() => {
    onDirtyChange(dirty);
    return () => onDirtyChange(false);
  }, [dirty, onDirtyChange]);
  useEffect(() => {
    if (!dirty) return;
    const protect = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', protect);
    return () => window.removeEventListener('beforeunload', protect);
  }, [dirty]);
  const open = (item?: ShootTransaction) => {
    if (!onBeforeOpen()) return;
    const value: FormValue = item
      ? { ...item, amount: formatCrmMoneyInput(item.amountMinor) }
      : {
          id: crypto.randomUUID(),
          kind: 'Income',
          amount:
            summary.remaining !== null && summary.remaining > 0
              ? formatCrmMoneyInput(summary.remaining)
              : '',
          date: todayInLisbon(),
          description: '',
          purpose:
            summary.received > 0 && summary.remaining !== null && summary.remaining > 0
              ? 'Balance'
              : 'Other',
        };
    setEditingTransaction(Boolean(item));
    setForm(value);
    setOriginal(JSON.stringify(value));
    setError('');
  };
  const close = () => {
    if (saving) return;
    if (dirty && !window.confirm('Discard unsaved transaction?')) return;
    onReset();
    setForm(null);
    setError('');
  };
  const save = async () => {
    if (!form || saving) return;
    const amountMinor = parseTransactionAmount(form.amount);
    if (!amountMinor) {
      setError('Enter a positive amount with at most two decimal places.');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date) || !Number.isFinite(Date.parse(form.date))) {
      setError('Choose a valid date.');
      return;
    }
    const entry: ShootTransaction = {
      id: form.id,
      kind: form.kind,
      amountMinor,
      date: form.date,
      description: form.description.trim(),
      purpose: form.kind === 'Expense' ? 'Other' : form.purpose,
    };
    setSaving(true);
    setError('');
    try {
      if (await onSave([...items.filter((item) => item.id !== entry.id), entry])) setForm(null);
      else
        setError(
          'Could not save. Your draft is kept. Review the error on the shoot and try again.',
        );
    } catch {
      setError('Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    if (!deleting || saving) return;
    setSaving(true);
    setError('');
    try {
      if (await onSave(items.filter((item) => item.id !== deleting.id))) setDeleting(null);
      else setError('Could not delete. Please try again.');
    } catch {
      setError('Could not delete. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <section className="shoot-money crm-detail-section" aria-labelledby="shoot-money-title">
      <div className="shoot-money__compact-heading" data-paid={paid || undefined}>
        <div className="shoot-money__label">
          <h2 id="shoot-money-title" className="crm-section-title">
            Money
          </h2>
          <button
            className="shoot-button shoot-button--icon shoot-money__add"
            aria-label="Add transaction"
            title="Add transaction"
            disabled={busy || disabled}
            onClick={() => open()}
          >
            <Plus size={16} aria-hidden="true" />
          </button>
        </div>
        <p
          className={`shoot-money__received${paid ? ' shoot-money__received--earned' : ''}`}
          aria-label={
            paid
              ? `Earned ${money(summary.net)}`
              : `Received ${money(summary.received)}${price !== null ? ` of ${money(price)}` : ''}`
          }
          data-negative={(paid && summary.net < 0) || undefined}
        >
          {paid ? <strong>{money(summary.net)}</strong> : money(summary.received)}
          {!paid && price !== null ? (
            <span className="shoot-money__price"> / {money(price)}</span>
          ) : null}
        </p>
      </div>
      {!paid && summary.percent !== null ? (
        <div
          className="shoot-money__progress"
          role="progressbar"
          aria-label="Payment progress"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(summary.percent)}
          aria-valuetext={summary.status}
        >
          <span style={{ width: `${summary.percent}%` }} />
        </div>
      ) : null}
      {!paid && summary.expenses > 0 ? (
        <div className="shoot-money__equation" aria-label="Income minus expenses equals net">
          <span title="Income" aria-label={`Income: ${money(summary.received)}`}>
            {money(summary.received)}
          </span>
          <span aria-hidden="true">−</span>
          <span title="Expenses" aria-label={`Expenses: ${money(summary.expenses)}`}>
            {money(summary.expenses)}
          </span>
          <span aria-hidden="true">=</span>
          <strong
            title="Net"
            aria-label={`Net: ${money(summary.net)}`}
            data-negative={summary.net < 0}
          >
            {money(summary.net)}
          </strong>
        </div>
      ) : null}
      <button
        className="shoot-money__history-toggle"
        aria-haspopup="dialog"
        onClick={() => setHistory(!history)}
      >
        History · {items.length}
      </button>
      <Dialog.Root open={history && !form && !deleting} onOpenChange={setHistory}>
        <Dialog.Portal container={portal}>
          <Dialog.Overlay className="shoot-panel__overlay" />
          <CompactDialogContent
            title="Transaction history"
            className="shoot-money-history-dialog"
            closeLabel="Close transaction history"
            onClose={() => setHistory(false)}
          >
            <div className="shoot-money__history">
              {items.length === 0 ? (
                <p>No transactions yet.</p>
              ) : (
                <ul>
                  {[...items]
                    .sort((a, b) => b.date.localeCompare(a.date) || a.id.localeCompare(b.id))
                    .map((item) => (
                      <li key={item.id}>
                        <div>
                          <strong>
                            {item.description || (item.kind === 'Expense' ? 'Expense' : 'Payment')}
                          </strong>
                          <small>
                            {new Intl.DateTimeFormat('en-GB', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                              timeZone: 'UTC',
                            }).format(new Date(`${item.date}T12:00:00Z`))}{' '}
                            · {item.kind}
                          </small>
                        </div>
                        <span className="shoot-money__amount">
                          {item.kind === 'Income' ? '+' : '−'}
                          {money(item.amountMinor)}
                        </span>
                        <div className="shoot-money__entry-actions">
                          <button
                            className="shoot-button shoot-button--outline shoot-button--icon"
                            aria-label={`Edit ${item.description || 'transaction'}`}
                            disabled={busy || disabled}
                            onClick={() => open(item)}
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            className="shoot-button shoot-button--outline shoot-button--icon"
                            aria-label={`Delete ${item.description || 'transaction'}`}
                            disabled={busy || disabled}
                            onClick={() => {
                              if (onBeforeOpen()) {
                                setError('');
                                setDeleting(item);
                              }
                            }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </li>
                    ))}
                </ul>
              )}
            </div>
          </CompactDialogContent>
        </Dialog.Portal>
      </Dialog.Root>
      <Dialog.Root
        open={form !== null}
        onOpenChange={(value) => {
          if (!value) close();
        }}
      >
        <Dialog.Portal container={portal}>
          <Dialog.Overlay className="shoot-panel__overlay" />
          <Dialog.Content
            ref={capturePanel}
            inert={form === null}
            className="shoot-panel shoot-money-form dui-compact-panel"
            data-compact-panel
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              const selector = window.matchMedia('(max-width: 1023px)').matches
                ? '[aria-label="Close transaction"]'
                : 'input[inputmode="decimal"]';
              content.current?.querySelector<HTMLElement>(selector)?.focus({ preventScroll: true });
            }}
            aria-describedby={undefined}
            onPointerDownOutside={(event) => event.preventDefault()}
            onEscapeKeyDown={(event) => {
              if (saving) event.preventDefault();
            }}
          >
            <div className="shoot-panel__heading" data-panel-header>
              <Dialog.Title>
                {editingTransaction ? 'Edit transaction' : 'Add transaction'}
              </Dialog.Title>
              <button
                className="shoot-button shoot-button--outline shoot-button--icon"
                aria-label="Close transaction"
                disabled={saving}
                onClick={close}
              >
                <X size={20} />
              </button>
            </div>
            {displayForm ? (
              <form
                onKeyDown={(event) => {
                  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') {
                    event.preventDefault();
                    event.stopPropagation();
                    if (!busy && !disabled) void save();
                  }
                }}
                onSubmit={(event) => {
                  event.preventDefault();
                  void save();
                }}
              >
                <fieldset ref={bodyRef} data-panel-body disabled={saving || busy || disabled}>
                  <legend className="sr-only">Type</legend>
                  <div className="shoot-money__type">
                    {(['Income', 'Expense'] as const).map((kind) => (
                      <button
                        key={kind}
                        type="button"
                        aria-pressed={displayForm.kind === kind}
                        className={`shoot-button ${displayForm.kind === kind ? 'shoot-button--primary' : 'shoot-button--outline'}`}
                        onClick={() => {
                          let amount = displayForm.amount;
                          if (!items.some((item) => item.id === displayForm.id)) {
                            const remaining =
                              summary.remaining !== null && summary.remaining > 0
                                ? formatCrmMoneyInput(summary.remaining)
                                : '';
                            if (
                              kind === 'Expense' &&
                              displayForm.kind === 'Income' &&
                              amount === remaining
                            )
                              amount = '';
                            if (kind === 'Income' && !amount) amount = remaining;
                          }
                          setForm({ ...displayForm, kind, amount });
                        }}
                      >
                        {kind}
                      </button>
                    ))}
                  </div>
                  <div className="shoot-money__fields">
                    <label>
                      Amount ({currency})
                      <input
                        required
                        inputMode="decimal"
                        value={displayForm.amount}
                        onChange={(e) => setForm({ ...displayForm, amount: e.target.value })}
                      />
                    </label>
                    <label>
                      Date
                      <input
                        required
                        type="date"
                        value={displayForm.date}
                        onChange={(e) => setForm({ ...displayForm, date: e.target.value })}
                      />
                    </label>
                  </div>
                  <label>
                    Description
                    <input
                      placeholder="Optional"
                      maxLength={500}
                      value={displayForm.description}
                      onChange={(e) => setForm({ ...displayForm, description: e.target.value })}
                    />
                  </label>
                </fieldset>
                <footer className="shoot-money__footer" data-panel-footer>
                  {error ? <p role="alert">{error}</p> : null}
                  <div className="shoot-money__actions">
                    <button
                      type="button"
                      className="shoot-button shoot-button--outline"
                      disabled={saving}
                      onClick={close}
                    >
                      Cancel
                    </button>
                    <button
                      className="shoot-button shoot-button--primary"
                      aria-busy={saving || busy}
                      disabled={saving || busy || disabled}
                    >
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </footer>
              </form>
            ) : null}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      <Dialog.Root
        open={deleting !== null}
        onOpenChange={(value) => {
          if (!value && !saving) {
            onReset();
            setDeleting(null);
          }
        }}
      >
        <Dialog.Portal container={portal}>
          <Dialog.Overlay className="shoot-panel__overlay" />
          <CompactDialogContent
            title="Delete transaction?"
            description={
              <>
                Remove {deleting ? money(deleting.amountMinor) : ''} from this shoot’s history.
                Totals will be recalculated.
              </>
            }
            busy={saving}
            onClose={() => {
              onReset();
              setDeleting(null);
            }}
            footer={
              <div className="shoot-money__actions">
                <button
                  className="shoot-button shoot-button--outline"
                  disabled={saving}
                  onClick={() => {
                    onReset();
                    setDeleting(null);
                  }}
                >
                  Keep transaction
                </button>
                <button
                  className="shoot-button shoot-button--danger"
                  disabled={saving || disabled}
                  onClick={() => void remove()}
                >
                  {saving ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            }
          >
            {error ? <p role="alert">{error}</p> : null}
          </CompactDialogContent>
        </Dialog.Portal>
      </Dialog.Root>
    </section>
  );
}
